import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import { ArrowLeft, Receipt, Trash2 } from "lucide-react";

const STATUSES = ["new_lead", "scheduled", "in_progress", "completed", "invoiced", "paid"];

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);

  async function load() {
    const { data } = await api.get(`/jobs/${id}`);
    setData(data);
  }
  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, [id]);

  async function update(patch) {
    await api.patch(`/jobs/${id}`, patch);
    load();
  }

  async function createInvoice() {
    const due = new Date();
    due.setDate(due.getDate() + 7);
    await api.post("/invoices", {
      job_id: id, amount: data.job.price,
      due_date: due.toISOString().slice(0, 10), status: "unpaid",
    });
    nav("/invoices");
  }

  async function del() {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    nav("/jobs");
  }

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const { job, customer, employee } = data;

  return (
    <div className="p-8 lg:p-10" data-testid="job-detail-page">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4"/> Back to job board
      </Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 capitalize">{job.service_type.replace("_", " ")}</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">{customer?.name}</h1>
          <div className="text-sm text-slate-500 mt-1">{job.date} · {job.time}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={job.status}/>
          {job.status === "completed" && (
            <button onClick={createInvoice} data-testid="create-invoice-btn"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-2 rounded-md transition-colors">
              <Receipt className="w-4 h-4"/> Create Invoice
            </button>
          )}
          <button onClick={del} data-testid="delete-job-btn"
            className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 text-sm px-3 py-2 rounded-md transition-colors">
            <Trash2 className="w-4 h-4"/>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Address</div>
            <div className="text-sm text-slate-700 mt-1">{job.address || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</div>
            <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{job.notes || "—"}</div>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-100">
            <div>
              <div className="text-xs text-slate-500">Square footage</div>
              <div className="font-display font-semibold text-lg">{job.square_footage}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Rooms</div>
              <div className="font-display font-semibold text-lg">{job.rooms}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Price</div>
              <div className="font-display font-semibold text-lg">${Number(job.price).toFixed(2)}</div>
            </div>
          </div>
          {job.addons?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Add-ons</div>
              <div className="flex flex-wrap gap-2">
                {job.addons.map((a) => (
                  <span key={a} className="text-xs px-2 py-1 bg-slate-100 rounded">{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</label>
            <select data-testid="jd-status"
              value={job.status} onChange={(e) => update({ status: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Assigned cleaner</label>
            <select data-testid="jd-employee"
              value={job.assigned_employee_id || ""} onChange={(e) => update({ assigned_employee_id: e.target.value || null })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500">
              <option value="">Unassigned</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </div>
          {customer && (
            <div className="pt-4 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Customer</div>
              <Link to={`/customers/${customer.id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600">
                {customer.name}
              </Link>
              {customer.phone && <div className="text-sm text-slate-500">{customer.phone}</div>}
              {customer.email && <div className="text-sm text-slate-500">{customer.email}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
